import React, { memo, useCallback } from 'react'
import { Button, Dropdown, MenuProps, Space } from 'antd'
import { MoreOutlined, DownOutlined } from '@ant-design/icons'
import { useMediaQuery } from 'react-responsive'
import { classNames } from '../../../lib/classNames/classNames'
import { AnyActionButton, DropdownActionButton, CardActionsProps } from './types'
import styles from './card-actions.module.css'

export const CardActions: React.FC<CardActionsProps> = memo(props => {
  const { primaryAction, secondaryActions = [], className, onMobileDropdownAction } = props
  const isMobile = useMediaQuery({ maxWidth: 768 })

  const renderButton = useCallback((action: AnyActionButton) => {
    if (action.type === 'dropdown') {
      const dropdownAction = action as DropdownActionButton
      const menuItems: MenuProps['items'] = dropdownAction.menuItems.map(item => ({
        key: item.key,
        label: (
          <div
            className={classNames(styles.menuItem, {
              [styles.menuItemDanger]: item.disabled,
            })}
            onClick={item.onClick}
          >
            {item.icon && <span className={styles.menuItemIcon}>{item.icon}</span>}
            {item.label}
          </div>
        ),
        disabled: item.disabled,
      }))

      return (
        <Dropdown
          key={action.key}
          menu={{
            items: menuItems,
            style: {
              maxHeight: '400px',
              overflowY: 'auto',
              width: '100%',
              maxWidth: '300px',
            },
          }}
          trigger={['click']}
          placement="bottomRight"
          disabled={action.disabled}
        >
          <Button
            type="default"
            danger={action.danger}
            disabled={action.disabled}
            loading={action.loading}
            icon={action.icon}
          >
            <Space>
              {action.label}
              <DownOutlined />
            </Space>
          </Button>
        </Dropdown>
      )
    }

    return (
      <Button
        key={action.key}
        type={action.type || 'default'}
        danger={action.danger}
        disabled={action.disabled}
        onClick={action.onClick}
        loading={action.loading}
        icon={action.icon}
      >
        {action.label}
      </Button>
    )
  }, [])

  const createMenuItems = useCallback((): MenuProps['items'] => {
    return secondaryActions.map(action => {
      if (action.type === 'dropdown') {
        return {
          key: action.key,
          label: (
            <div
              className={classNames(styles.menuItem, {
                [styles.menuItemDanger]: action.danger,
              })}
              onClick={() => {
                const dropdownAction = action as DropdownActionButton
                if (onMobileDropdownAction) {
                  onMobileDropdownAction(action.key, dropdownAction)
                }
              }}
            >
              {action.icon && <span className={styles.menuItemIcon}>{action.icon}</span>}
              {action.label}
            </div>
          ),
          disabled: action.disabled,
        }
      }

      return {
        key: action.key,
        label: (
          <div
            className={classNames(styles.menuItem, {
              [styles.menuItemDanger]: action.danger,
            })}
            onClick={action.onClick}
          >
            {action.icon && <span className={styles.menuItemIcon}>{action.icon}</span>}
            {action.label}
          </div>
        ),
        disabled: action.disabled,
      }
    })
  }, [secondaryActions, onMobileDropdownAction])

  if (isMobile) {
    return (
      <div className={classNames(styles.container, {}, [styles.mobileActions, className])}>
        {primaryAction && primaryAction.type === 'dropdown' ? (
          <Dropdown
            menu={{
              items: (primaryAction as DropdownActionButton).menuItems.map(item => ({
                key: item.key,
                label: (
                  <div
                    className={classNames(styles.menuItem, {
                      [styles.menuItemDanger]: item.disabled,
                    })}
                    onClick={item.onClick}
                  >
                    {item.icon && <span className={styles.menuItemIcon}>{item.icon}</span>}
                    {item.label}
                  </div>
                ),
                disabled: item.disabled,
              })),
              style: {
                maxHeight: '300px',
                overflowY: 'auto',
                width: '200px',
              },
            }}
            trigger={['click']}
            placement="bottomRight"
          >
            <Button
              type="primary"
              className={styles.primaryButton}
              disabled={primaryAction.disabled}
              loading={primaryAction.loading}
            >
              {primaryAction.label}
            </Button>
          </Dropdown>
        ) : (
          <Button
            type={primaryAction?.type || 'primary'}
            danger={primaryAction?.danger}
            disabled={primaryAction?.disabled}
            onClick={primaryAction?.onClick}
            loading={primaryAction?.loading}
            className={styles.primaryButton}
          >
            {primaryAction?.label}
          </Button>
        )}
        {secondaryActions.length > 0 && (
          <Dropdown
            menu={{
              items: createMenuItems(),
              style: {
                maxHeight: '400px',
                overflowY: 'auto',
                width: '100%',
                maxWidth: '400px',
              },
            }}
            trigger={['click']}
            placement="bottomRight"
          >
            <Button icon={<MoreOutlined />} className={styles.contextMenuButton} />
          </Dropdown>
        )}
      </div>
    )
  }

  const allActions = [...secondaryActions]
  if (primaryAction) {
    allActions.unshift(primaryAction)
  }

  return (
    <div className={classNames(styles.container, {}, [styles.desktopActions, className])}>
      {allActions.map(renderButton)}
    </div>
  )
})

CardActions.displayName = 'CardActions'
